import { useContext, useRef, useState } from "react";
import TenantContext from "../../context/tenant/TenantContext";
import { haveValue, toast } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import AnalysisLoading from "../../components/AnalysisLoading/AnalysisLoading";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import { FileInput, Button, Icon } from "../../components/design";
import styles from "../onboarding/RequiredDocumentsModal.module.css";
import "./tenantCheckSteps.css";

const MAX_FILE_SIZE = 8 * 1024 * 1024;

const TenantChecksIncomeModel = ({ user, formHandler }) => {
  const { _t } = useTranslator();
  const { uploadBankStatement } = useContext(TenantContext);
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const reCheck = new URLSearchParams(window.location.search).get("reCheck");

  const stageFile = (file) => {
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error(_t("fileTypeNotAllowed"));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        _t("fileUploadedExceedsEightMbFileLimit", "Files can be up to 8 MB")
      );
      return;
    }

    setPendingFile(file);
  };

  const handleSubmit = async () => {
    if (!pendingFile || !user?._id) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", pendingFile);
      formData.append("tenant_id", user._id);
      uploadBankStatement(formData);
      setPendingFile(null);

      if (haveValue(reCheck) && reCheck) {
        window.history.back();
      } else {
        await formHandler.next();
      }
    } catch (error) {
      // uploadBankStatement owns the user-facing API error message.
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="tc-step-page">
      <section className="tc-step">
        {uploading ? (
          <AnalysisLoading
            size="compact"
            title={_t("analyzingYourStatement", "Analyzing your statement")}
            subtitle={_t(
              "analyzingSubtitle",
              "A few seconds. Don't close this window."
            )}
            steps={[
              _t("readingTheDocument", "Reading the document"),
              _t("checkingItsInEuros", "Checking it's in euros"),
              _t(
                "checkingItCoversLast3Months",
                "Checking it covers the last 3 months"
              ),
            ]}
          />
        ) : (
          <>
            <AuthStepHeader
              onBack={() =>
                haveValue(reCheck) && reCheck
                  ? window.history.back()
                  : formHandler.prev()
              }
              title={_t(
                "uploadTheirBankStatement",
                "Upload their bank statement"
              )}
              subtitle={_t(
                "uploadTheirBankStatementSubtitle",
                "Upload a PDF bank statement covering the last 3 months"
              )}
            />

            <div
              role="button"
              tabIndex={0}
              onClick={() => !pendingFile && fileRef.current?.click()}
              onKeyDown={(event) => {
                if (
                  !pendingFile &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  fileRef.current?.click();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOver(false);
                stageFile(event.dataTransfer.files?.[0]);
              }}
              className={`${styles.uploadCard} ${dragOver ? styles.dragging : ""} tc-upload ${pendingFile ? "has-file" : ""}`}
            >
              <FileInput
                ref={fileRef}
                accept="application/pdf,.pdf"
                hidden
                onChange={(event) => {
                  stageFile(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              {pendingFile ? (
                <div className="tc-upload__file">
                  <span
                    className="tc-upload__file-name"
                    title={pendingFile.name}
                  >
                    {pendingFile.name} ·{" "}
                    {(pendingFile.size / (1024 * 1024)).toFixed(1)} MB
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    iconStart={<Icon name="close" size={16} />}
                    aria-label={_t("removeFile", "Remove file")}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingFile(null);
                    }}
                  />
                </div>
              ) : (
                <div className="tc-upload__empty">
                  <div className={styles.uploadIcon} aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 13v8" />
                      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                      <path d="m8 17 4-4 4 4" />
                    </svg>
                  </div>
                  <h2>{_t("uploadYourStatement", "Upload your statement")}</h2>
                  <p className={styles.uploadHint}>
                    {_t(
                      "bankStatementFileHint",
                      "Drag it here or click to select · PDF · up to 8 MB"
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="tc-step-actions">
              <Button
                size="lg"
                onClick={() =>
                  pendingFile ? handleSubmit() : fileRef.current?.click()
                }
              >
                {pendingFile
                  ? _t("analyzeStatement", "Analyze statement")
                  : _t("selectFile", "Select file")}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default TenantChecksIncomeModel;
